/*
This file should only contain the code for the plugin, not any specific instance of it being used. Please put your code for pickit in the file for the component/widget you are adding a date picket to.

For date format options please see:
http://blog.stevenlevithan.com/archives/date-time-format

USAGE:
	import pickit from '~/utils/pickit'
	const datePicker = document.querySelector('.datePicker input')
	pickit(datePicker)

PUG:
	//this project is setup to allow the following usage by default, however this can be changed or expanded upon in components/forms.js
	.datePicker
		input(type="text")
		---or---
		input(data-start-date="today", data-end-date="9/26/2019", data-days="[1, 2, 3, 4, 5]")
		---or---
		input(data-start-date="tomorrow", data-end-date="10/25/2019")
		---or---
		input(data-start-date="yesterday")
		---or---
		input(data-start-date="7/22/2019", data-format="dd-mm-yyyy")
*/
import dateformat from 'dateformat';

import { util } from '~/core';

import addEvents from '~/utils/addEvents';
import bodyLock from '~/utils/bodyLock';
import createElement from '~/utils/createElement';
import isChild from '~/utils/isChild';

export default function pickit(_el) {
	//handle different kinds of elements being passeed (selector string, node lists/arrays, or single elements)
	const _elements =
		typeof _el == 'string' ? [...document.querySelectorAll(_el)] : _el.length ? [..._el] : [_el];
	//remove calendars from dom
	const clean = () => {
		const _calendars = [...document.querySelectorAll('.pickit')];
		_calendars.forEach(cal => cal.parentElement.removeChild(cal));
		if (window.matchMedia('(max-width:600px)').matches) {
			bodyLock.clear();
		}
	};

	addEvents(document, 'click', e => {
		if (!isChild(e.target, '.pickit-wrapper') || e.target.matches('.pickit-wrapper')) {
			clean();
		}
	})

	//loop for each individual element
	_elements.forEach(_element => {
		//function for selecting a date
		const select = (date, { ...params }) => {
			const selection = new Date(
				new Date().setFullYear(date.getFullYear(), date.getMonth(), date.getDate())
			);
			_element.value = dateformat(selection, params.format);
			clean();
			_element.focus()
			_element.blur()
		};

		const create_monthPick = ({ ...params }) => {
			const _dateTitleMonth = createElement('span', { class: 'pickit-titleMonth' });
			const _dateTitleMonthValue = createElement(
				'span',
				{ class: 'pickit-titleMonthValue', tabindex: 0 },
				params.months[params.selected.getMonth()]
			);
			const _dateTitleMonthPicker = createElement('span', {
				class: 'pickit-titleMonthPicker'
			});

			//setup month picker
			params.months.forEach((month, i) => {
				let newMonth = new Date(params.selected.getFullYear(), i, params.selected.getDate());
				let inRangeStart = true;
				let inRangeEnd = true;
				const _month = createElement(
					'span',
					{ class: 'pickit-titleMonthPicker-month', tabindex: 0 },
					month
				);

				inRangeStart = !params.startDate
					? true
					: newMonth.getTime() >= params.startDate.getTime()
					? true
					: false;
				inRangeEnd = !params.endDate
					? true
					: newMonth.getTime() <= params.endDate.getTime()
					? true
					: false;
				if (!inRangeStart || !inRangeEnd) {
					_month.classList.add('pickit-titleMonthPicker-month--disabled');
				}
				addEvents(_month, 'click.stop.prevent enter.stop.prevent', e => create(newMonth));
				_dateTitleMonthPicker.insertAdjacentElement('beforeend', _month);
			});
			addEvents(_dateTitleMonthValue, 'click.stop enter.stop', e => {
				_dateTitleMonth.classList.add('is-edit');
			});
			_dateTitleMonth.insertAdjacentElement('beforeEnd', _dateTitleMonthValue);
			_dateTitleMonth.insertAdjacentElement('beforeEnd', _dateTitleMonthPicker);

			return _dateTitleMonth;
		};

		const create_yearPick = ({ ...params }) => {
			const _dateTitleYear = createElement('span', { class: 'pickit-titleYear' });
			const _dateTitleYearValue = createElement(
				'span',
				{ class: 'pickit-titleYearValue', tabindex: 0 },
				params.selected.getFullYear()
			);
			const _dateTitleYearInput = createElement(
				'input',
				{ class: 'pickit-titleYearInput', type: 'text', minlength: 4, maxlength: 4 },
				params.selected.getFullYear()
			);

			//setup year input
			addEvents(_dateTitleYearValue, 'click.stop.prevent enter.stop.prevent', e => {
				_dateTitleYear.classList.add('is-edit');
				_dateTitleYearInput.value = '';
				_dateTitleYearInput.focus();
			});
			addEvents(_dateTitleYearInput, 'enter.stop.prevent', e =>
				_dateTitleYearInput.blur()
			);
			addEvents(_dateTitleYearInput, 'submit.stop.prevent', e =>
				_dateTitleYearInput.blur()
			);
			addEvents(_dateTitleYearInput, 'blur.stop.prevent', e => {
				if (_dateTitleYearInput.value.length == 4 && parseInt(_dateTitleYearInput.value)) {
					let newYear = new Date(
						parseInt(_dateTitleYearInput.value),
						params.selected.getMonth(),
						params.selected.getDate()
					);
					let inRangeStart = true;
					let inRangeEnd = true;

					inRangeStart = !params.startDate
						? true
						: newYear.getTime() >= params.startDate.getTime()
						? true
						: false;
					inRangeEnd = !params.endDate
						? true
						: newYear.getTime() <= params.endDate.getTime()
						? true
						: false;

					if (inRangeStart && inRangeEnd) {
						create(newYear);
					} else {
						_dateTitleYear.classList.remove('is-edit');
					}
				} else {
					_dateTitleYear.classList.remove('is-edit');
				}
			});
			_dateTitleYear.insertAdjacentElement('beforeend', _dateTitleYearValue);
			_dateTitleYear.insertAdjacentElement('beforeend', _dateTitleYearInput);

			return _dateTitleYear;
		};

		//create head row of calendar
		const create_head = ({ ...params }) => {
			const _head = createElement('div', { class: 'pickit-head' });
			const _prev = createElement('div', { class: 'pickit-nav', tabindex: 0 });
			const _next = createElement('div', { class: 'pickit-nav', tabindex: 0 });
			const prevMonth = new Date(params.selected.getFullYear(), params.selected.getMonth() - 1, 1);
			const nextMonth = new Date(params.selected.getFullYear(), params.selected.getMonth() + 1, 1);
			const _dateTitle = createElement('span', { class: 'pickit-title' });

			//disable dates before start date and after end date (if provided in markup)
			if (params.startDate) {
				params.startDate = new Date(new Date(params.startDate).setDate(1));
				if (prevMonth.getTime() < params.startDate.getTime()) {
					_prev.classList.add('pickit-nav--disabled');
					_prev.removeAttribute('tabindex');
				}
			}
			if (params.endDate) {
				params.endDate = new Date(params.endDate.getFullYear(), params.endDate.getMonth() + 1, 0);
				if (nextMonth.getTime() > params.endDate.getTime()) {
					_next.classList.add('pickit-nav--disabled');
					_next.removeAttribute('tabindex');
				}
			}

			//setup nav buttons
			addEvents(_prev, 'click.stop enter.stop', e => create(prevMonth, 'prev'));
			addEvents(_next, 'click.stop enter.stop', e => create(nextMonth, 'next'));

			const _dateTitleMonth = create_monthPick(params);
			const _dateTitleYear = create_yearPick(params);

			_dateTitle.insertAdjacentElement('beforeend', _dateTitleMonth);
			_dateTitle.insertAdjacentElement('beforeend', _dateTitleYear);

			_head.insertAdjacentElement('beforeend', _prev);
			_head.insertAdjacentElement('beforeend', _dateTitle);
			_head.insertAdjacentElement('beforeend', _next);

			return { _head, _prev, _next };
		};

		//create day row of calendar
		const create_days = ({ ...params }) => {
			const _days = createElement('div', { class: 'pickit-days' });

			params.days.forEach(day =>
				_days.insertAdjacentHTML('beforeend', `<div class="pickit-day">${day}</div>`)
			);
			return _days;
		};

		// create body of calendar (date section)
		const create_dates = ({ ...params }) => {
			const _dates = createElement('div', { class: 'pickit-dates' });
			let startDay = new Date(
				params.selected.getFullYear(),
				params.selected.getMonth(),
				1
			).getDay();
			let daysInMonth = new Date(
				params.selected.getFullYear(),
				params.selected.getMonth() + 1,
				0
			).getDate();
			let i = 0;
			let j = 0;
			//Create empty body cells (previous month)

			while (i++ < startDay) {
				let _date = createElement('div', { class: 'pickit-date' }, i);
				_date.classList.add('pickit-date--empty');
				_dates.insertAdjacentElement('beforeend', _date);
			}

			//create filled body cells (this month)
			while (j++ < daysInMonth) {
				const _date = createElement('div', { class: 'pickit-date', tabindex: 0 }, j);
				let thisDate = new Date(
					`${params.selected.getMonth() + 1}/${j}/${params.selected.getFullYear()}`
				);

				if (thisDate.getTime() == params.today.getTime()) {
					_date.classList.add('pickit-date--today');
				}
				if (thisDate.getTime() == params.input.getTime()) {
					_date.classList.add('pickit-date--selected');
				}
				if (params.startDate && thisDate.getTime() < params.startDate.getTime()) {
					_date.classList.add('pickit-date--disabled');
					_date.removeAttribute('tabindex');
				}
				if (params.enabledDays && !params.enabledDays.includes(thisDate.getDay())) {
					_date.classList.add('pickit-date--disabled');
					_date.removeAttribute('tabindex');
				}
				if (params.endDate && thisDate.getTime() > params.endDate.getTime()) {
					_date.classList.add('pickit-date--disabled');
				}
				addEvents(
					_date,
					'click enter',
					e => (e.stopPropagation(), select(thisDate, params))
				);
				_dates.insertAdjacentElement('beforeend', _date);
			}

			return _dates;
		};

		// assemble all the parts of the calendar
		const create = (date, dir) => {
			const today = new Date(new Date().setHours(0, 0, 0, 0));
			const input = _element.value ? new Date(_element.value) : new Date();
			const selected = date ? new Date(date) : input;

			const months = [...new Array(12)].map((x, i) =>
				new Date(2001, i, 2).toLocaleString('default', { month: 'long' })
			);
			const days = [...new Array(7)].map((x, i) =>
				new Date(2001, 0, i).toLocaleString('default', { weekday: 'short' }).charAt(0)
			);

			let startDate = _element.getAttribute('data-start-date') || null;
			let endDate = _element.getAttribute('data-end-date') || null;
			let enabledDays = JSON.parse(_element.getAttribute('data-days')) || null;
			let format = _element.getAttribute('data-format') || 'mm/dd/yyyy';

			if (startDate) {
				startDate =
					startDate == 'today'
						? new Date(new Date().setHours(0, 0, 0, 0))
						: startDate == 'tomorrow'
						? new Date(new Date().setHours(0, 0, 0, 0) + 60 * 60 * 24 * 1000)
						: startDate == 'yesterday'
						? new Date(new Date().setHours(0, 0, 0, 0) - 60 * 60 * 24 * 1000)
						: new Date(new Date(startDate).setHours(0, 0, 0, 0));
			}
			if (endDate) {
				endDate = new Date(new Date(endDate).setHours(0, 0, 0, 0));
			}

			const params = {
				today,
				input,
				selected,
				days,
				startDate,
				endDate,
				months,
				enabledDays,
				format
			};

			const _calendar = createElement('div', { class: 'pickit is-mouse' });
			const _calendarInner = createElement('div', { class: 'pickit-inner' });
			const _head = create_head(params);
			const _days = create_days(params);
			const _dates = create_dates(params);

			clean();
			addEvents(_calendar, 'esc', clean);
			addEvents(_wrapper, 'mousedown', e => _calendar.classList.add('is-mouse'));
			addEvents(_wrapper, 'keydown', e => _calendar.classList.remove('is-mouse'));

			_calendar.insertAdjacentElement('beforeend', _calendarInner);
			_calendarInner.insertAdjacentElement('beforeend', _head._head);
			_calendarInner.insertAdjacentElement('beforeend', _days);
			_calendarInner.insertAdjacentElement('beforeend', _dates);
			_wrapper.insertAdjacentElement('beforeend', _calendar);

			if (window.matchMedia('(max-width:600px)').matches) {
				bodyLock.lock(_calendar);
			}

			if (dir == 'prev') {
				_head._prev.focus();
			}
			if (dir == 'next') {
				_head._next.focus();
			}
		};

		//initialize calendar
		const init = () => {
			_wrapper = _element.parentElement;
			_wrapper.classList.add('pickit-wrapper');
			_element.classList.add('pickit-input');
			// _element.setAttribute('readonly', 'readonly');

			addEvents(_element, 'click.stop.prevent touch.stop.prevent', () => create());
		};
		let _wrapper;
		init();
	});
}

util.pickit = pickit;
